import { IChangeRequestUpdateInput } from "@/Common/api/types/ChangeRequests";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { ChangeRequestResponseFragment } from "../common/fragments";

export const createChangeRequest = async (params: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	userId: string;
	input: {
		description?: string;
		issueSummary: string;
		origin: string;
		position: string;
	};
}) => {
	const { locationId, input, commissionCycleId, payPeriodId, userId } = params;
	const query = gql`
		mutation CreateChangeRequest(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$userId: String!
			$input: CreateChangeRequestInput!
		) {
			createChangeRequest(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				userId: $userId
				input: $input
			) {
				...ChangeRequestResponse
			}
		}
		${ChangeRequestResponseFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		userId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};

export const updateChangeRequest = async (params: IChangeRequestUpdateInput) => {
	const { locationId, input, commissionCycleId, payPeriodId, userId, requestId, position } = params;

	const query = gql`
		mutation updateChangeRequest(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$userId: String!
			$requestId: String!
			$position: String!
			$input: UpdateChangeRequestInput
		) {
			updateChangeRequest(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				userId: $userId
				requestId: $requestId
				position: $position
				input: $input
			) {
				...ChangeRequestResponse
			}
		}
		${ChangeRequestResponseFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		userId,
		requestId,
		input,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};

export const generateAllWashouts = async ({
	locationId,
	commissionCycleId,
	payPeriodId,
	positions,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	positions: string[];
}) => {
	const query = gql`
		mutation generateAllWashouts($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $positions: [String!]!) {
			generateAllWashouts(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				positions: $positions
			) {
				success
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		positions,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.generateAllWashouts;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
};

export async function retractAllWashouts({
	locationId,
	commissionCycleId,
	payPeriodId,
	userId,
	positions,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	userId: string;
	positions: string[];
}) {
	const query = gql`
		mutation retractAllWashouts(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$userId: String!
			$positions: [String!]!
		) {
			retractAllWashouts(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				userId: $userId
				positions: $positions
			) {
				locationId
				commissionCycleId
				payPeriodId
				statusSetBy
				statusSetOn
				positionStatus {
					position
					status
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		userId,
		positions,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.retractAllWashouts;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
